import React, {FocusEvent, ReactElement} from "react";
import {BaseInput} from "/src/types";
import {css} from "/src/utils/utils";
import Check from "/src/svg/check.svg";
import ErrorMessage from "../error-message/ErrorMessage";
import FormError from "/src/svg/form-error.svg";
import Icon from "../../icon/Icon";
import Label from "../label/Label";

import * as styles from "./textarea.module.scss";

interface TextareaProps extends BaseInput {
  placeholder?: string;
  onChange: (str: string) => unknown;
  onFocus?: (e: FocusEvent<HTMLTextAreaElement>) => unknown;
  onBlur?: (e: FocusEvent<HTMLTextAreaElement>) => unknown;
  maxLength?: number;
  rows?: number;
  transparentBg?: boolean;
}

const Textarea = (props: TextareaProps): ReactElement => {
  const {
    label = "",
    required = false,
    name,
    value = undefined,
    placeholder = "",
    errorMessage = "",
    status,
    disabled = false,
    readOnly = false,
    onChange,
    onFocus,
    onBlur,
    maxLength,
    darkMode = false,
    rows = 4,
    transparentBg = false,
  } = props;

  const [isFocused, setIsFocused] = React.useState<boolean>(false);

  const isInvalid = status === "error";
  const isValid = status === "success";

  const onTextareaFocus = (e: FocusEvent<HTMLTextAreaElement>) => {
    // if status is not error/success, set focus state
    if (!isInvalid && !isValid) {
      setIsFocused(true);
    }

    if (onFocus) {
      onFocus(e);
    }
  };

  const onTextareaBlur = (e: FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(false);

    if (onBlur) {
      onBlur(e);
    }
  };

  return (
    <label
      className={css(
        styles.textarea,
        darkMode ? styles.textareaDark : "",
        transparentBg ? styles.textareaTransparent : "",
        isFocused ? styles.textareaFocused : "",
        isInvalid ? styles.textareaError : "",
        isValid ? styles.textareaSuccess : "",
        disabled ? styles.textareaDisabled : ""
      )}
    >
      <Label
        label={label}
        required={required}
        darkMode={darkMode}
        disabled={disabled}
      />
      <div className={css(styles.textarea__wrapper)}>
        <textarea
          data-lpignore="true"
          className={css("p", styles.textarea__input)}
          name={name}
          value={value}
          placeholder={placeholder}
          onFocus={onTextareaFocus}
          onBlur={onTextareaBlur}
          onChange={e => onChange(e.target.value)}
          disabled={disabled}
          readOnly={readOnly}
          maxLength={maxLength}
          rows={rows}
          required={required}
        />
        {/* always leave a space for icon */}
        <div className={css(styles.textarea__iconWrapper)}>
          {(isInvalid || isValid) && (
            <Icon
              svg={!isValid ? FormError : Check}
              role="presentation"
              classes={styles.textarea__icon}
            />
          )}
        </div>
      </div>
      <ErrorMessage message={errorMessage} visible={isInvalid} />
    </label>
  );
};

export default Textarea;
